import { PlusOutlined } from '@ant-design/icons';
import AppButton from '@nxsys/components/AppButton';
import { AppConfirmationDelete } from '@nxsys/components/AppConfirmationDeleteModal';
import AppsContent from '@nxsys/components/AppsContainer/AppsContent';
import AppSelect from '@nxsys/components/AppSelect';
import AppTableContainer from '@nxsys/components/AppTableContainer';
import {
  defaultCO2EmissionsOptions,
  defaultVehicleTypeOptions,
} from '@nxsys/constants/AppConstants';
import { VehicleTypeEnum } from '@nxsys/constants/AppEnums';
import {
  useCreateBIKEmployee,
  useDeleteBIKEmployee,
  useUpdateBIKEmployee,
} from '@nxsys/hooks/api';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppDispatch, AppState } from '@nxsys/store';
import { employeeActions } from '@nxsys/store/slices/employee/reducer/employeeSlice';
import { GetBIKListingEmployeeListParams } from '@nxsys/store/slices/employee/types';
import {
  CreateBIKEmployeeParams,
  UpdateBIKEmployeeParams,
} from '@nxsys/types/api';
import { Icons } from '@nxsys/types/icons';
import { IBIK } from '@nxsys/types/models/BIK';
import { showCurrencyByCountry } from '@nxsys/utils';
import { formatMoney } from '@nxsys/utils/number-helper';
import { showToast } from '@nxsys/utils/toast';
import { ColumnType } from 'antd/lib/table';
import { toNumber } from 'lodash';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import CreateVehicleModal from './CreateVehicleModal';
import EditVehicleModal from './EditVehicleModal';
import { VehicleFormValues } from './VehicleForm';


type Props = {
  employeeId: string;
};

const Vehicle = ({ employeeId }: Props) => {
  const { messages } = useTypedIntl();
  const dispatch = useDispatch<AppDispatch>();
  const { payrollCountry } = useSelector((state: AppState) => state.appStore);

  const { list, loading, pagination, queryParams } = useSelector(
    (state: AppState) => state.employeeStore.bikListingList,
  );

  useEffect(() => {
    handleFetchData({
      page: 0,
      size: 20,
      types: [VehicleTypeEnum.CAR, VehicleTypeEnum.VAN],
    });
  }, []);

  const handleFetchData = (params: GetBIKListingEmployeeListParams) => {
    const newParams = {
      ...params,
      subcontractorId: employeeId,
    };
    dispatch(employeeActions.getBIKListingEmployeeList(newParams));
  };

  const refetchData = () => {
    handleFetchData({
      ...queryParams,
    });
  };

  const onChangePagination = (page: number, pageSize: number) => {
    const isPageSizeChanged = pageSize !== pagination.pageSize;
    const shouldResetPage = isPageSizeChanged && pagination.current > 0;
    handleFetchData({
      ...queryParams,
      page: shouldResetPage ? 0 : page - 1,
      size: pageSize,
    });
  };

  const columns: ColumnType<IBIK>[] = [
    {
      key: 'action',
      title: messages['common.action'],
      fixed: 'left',
      align: 'left',
      render: (record) => (
        <ul className="table-actions flex items-center justify-start">
          <li onClick={() => toggleEdit(record)}>
            <Icons.IconEdit />
          </li>
          <li onClick={() => handleDelete(record.id)}>
            <Icons.IconDelete />
          </li>
        </ul>
      ),
      width: 100,
    },
    {
      key: 'type',
      title: messages['common.type'],
      dataIndex: 'type',
      render: (value) =>
        value
          ? defaultVehicleTypeOptions.find((option) => option.value === value)
              ?.label
          : '-',
      width: 100,
    },
    {
      key: 'description',
      title: messages['common.description'],
      dataIndex: 'description',
    },
    {
      key: 'originalMarketValue',
      title: messages['common.originalMarketValue'],
      dataIndex: 'originalMarketValue',
      render: (value) =>
        formatMoney(value || 0, showCurrencyByCountry(payrollCountry)),
    },
    {
      key: 'co2Emissions',
      title: messages['common.co2Emissions'],
      dataIndex: 'co2Emissions',
      render: (value) =>
        value
          ? defaultCO2EmissionsOptions.find((option) => option.value === value)
              ?.label
          : '-',
    },
    {
      key: 'km',
      title: messages['common.kilometres'],
      dataIndex: 'value',
    },
    {
      key: 'totalKilometres',
      title: messages['common.totalKilometres'],
      dataIndex: 'totalKilometres',
    },
    {
      key: 'eeContribution',
      title: messages['common.employeeContribution'],
      dataIndex: 'eeContribution',
    },
    {
      key: 'totalEeContribution',
      title: messages['common.totalEmployeeContribution'],
      dataIndex: 'totalEeContribution',
      width: 250,
    },
    {
      key: 'startDate',
      title: messages['common.startDate'],
      dataIndex: 'startDate',
      render: (value) => (value ? moment(value).format('DD/MM/YYYY') : '-'),
    },
    {
      key: 'endDate',
      title: messages['common.endDate'],
      dataIndex: 'endDate',
      render: (value) => (value ? moment(value).format('DD/MM/YYYY') : '-'),
    },
  ];

  const [openCreateModal, setOpenCreateModal] = useState(false);
  const toggleCreate = () => {
    setOpenCreateModal((prev) => !prev);
  };
  const { run: createBIKEmployee } = useCreateBIKEmployee();
  const handleCreate = async (values: VehicleFormValues) => {
    const params: CreateBIKEmployeeParams = {
      value: toNumber(values.value),
      startDate: values.startDate,
      co2Emissions: values.co2Emissions?.value || null,
      originalMarketValue: toNumber(values.originalMarketValue),
      type: values.type?.value || null,
      description: values.description,
      endDate: values.endDate,
      totalKilometres: toNumber(values.totalKilometres),
      eeContribution: values.eeContribution,
      totalEeContribution: toNumber(values.totalEeContribution),
      subcontractorId: employeeId,
    };
    const res = await createBIKEmployee(params);
    if (res.success) {
      showToast({
        type: 'success',
        message: messages['common.addSuccess'],
      });
      toggleCreate();
      refetchData();
    }
  };

  const [openEditModal, setOpenEditModal] = useState(false);
  const [selectedData, setSelectedData] = useState<IBIK | null>(null);
  const toggleEdit = (record?: IBIK) => {
    setOpenEditModal((prev) => !prev);
    setSelectedData(record || null);
  };
  const { run: updateBIKEmployee } = useUpdateBIKEmployee();
  const handleUpdate = async (values: VehicleFormValues) => {
    const params: UpdateBIKEmployeeParams = {
      value: toNumber(values.value),
      startDate: values.startDate,
      co2Emissions: values.co2Emissions?.value || null,
      originalMarketValue: toNumber(values.originalMarketValue),
      type: values.type?.value || null,
      description: values.description,
      endDate: values.endDate,
      totalKilometres: toNumber(values.totalKilometres),
      eeContribution: values.eeContribution,
      totalEeContribution: toNumber(values.totalEeContribution),
      subcontractorId: employeeId,
    };
    const res = await updateBIKEmployee(selectedData!.id, params);
    if (res.success) {
      showToast({
        type: 'success',
        message: messages['common.updateSuccess'],
      });
      toggleEdit();
      refetchData();
    }
  };

  const { run: deleteBIKEmployee } = useDeleteBIKEmployee();
  const handleDelete = async (id: number) => {
    AppConfirmationDelete.open({
      title: messages['common.delete'],
      paragraph: messages['common.deleteConfirm'],
      onConfirm: async () => {
        const res = await deleteBIKEmployee(id);
        if (res.success) {
          showToast({
            type: 'success',
            message: messages['common.deleteSuccess'],
          });
          refetchData();
        }
      },
    });
  };

  const handleChangeType = (value: any) => {
    handleFetchData({
      ...queryParams,
      types: value ? [value.value] : [VehicleTypeEnum.CAR, VehicleTypeEnum.VAN],
    });
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-[16px]">
        <div>
          <AppSelect
            options={defaultVehicleTypeOptions}
            placeholder={messages['common.selectType']}
            onChange={handleChangeType}
          />
        </div>
        <AppButton
          type="primary"
          name={messages['common.addNew']}
          leftIcon={<PlusOutlined />}
          onClick={toggleCreate}
        />
      </div>
      <AppsContent>
        <AppTableContainer
          data={list || []}
          loading={loading}
          page={pagination.current}
          total={pagination.totalDocs}
          pageSize={pagination.pageSize}
          scroll={{ x: 2000, y: 700 }}
          titleEmpty={messages['common.noData']}
          columns={columns}
          onChangePagination={onChangePagination}
          noDataType={
            queryParams?.types?.length === 1 ? 'noDataFound' : 'noDataCreated'
          }
          addNewBtnOnClick={toggleCreate}
          dataTypeName={messages['common.vehicle']}
        />
      </AppsContent>
      <CreateVehicleModal
        isOpen={openCreateModal}
        toggle={toggleCreate}
        onSubmit={handleCreate}
        employeeId={employeeId}
      />
      {selectedData && (
        <EditVehicleModal
          isOpen={openEditModal}
          toggle={toggleEdit}
          onSubmit={handleUpdate}
          data={selectedData}
        />
      )}
    </div>
  );
};

export default Vehicle;
